import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { getOrderUuid, getSelectedExhibitionId } from '@app/app.reducer';
import { Store } from '@ngrx/store';
import {
  BookingPackageType,
  BookingProductType,
  PostProductBooking,
  PostProductBookingResponse,
  SharedBookedPersonIdTariffsTotalCount,
  isBookingProductTypePackage,
  isBookingProductTypeTariff
} from '@products/models/booking.model';
import { AppConstants } from '@shared/app-constants';

import { environment } from '@src/environments/environment';
import {
  getBookedProducts,
  getSharedBookedPersonIdTariffsTotalCount
} from '@store/products/booking/product/product-booking.selectors';
import { isEmpty, isNil } from 'lodash';
import { Observable, combineLatest, first, map, of } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class ProductBookingService {
  private readonly http = inject(HttpClient);
  private readonly store = inject(Store);

  setProductBookings$(
    bookingProductTypes: BookingProductType[],
    isPackageAdded = false
  ): Observable<PostProductBooking[]> {
    return combineLatest([
      this.store.select(getSelectedExhibitionId),
      this.store.select(getOrderUuid),
      this.store.select(getSharedBookedPersonIdTariffsTotalCount(bookingProductTypes))
    ]).pipe(
      first((combinedLatest) => combinedLatest.every((combinedItem) => !!combinedItem)),
      map(([eventId, orderUuid, sharedBookedPersonIdTariffsCount]) => {
        return this.mapBookingProductTypeToPostProductBooking(
          bookingProductTypes,
          eventId,
          orderUuid,
          sharedBookedPersonIdTariffsCount,
          isPackageAdded
        );
      })
    );
  }

  removeProductBookings$(bookingProductTypes: BookingProductType[]): Observable<PostProductBooking[]> {
    return combineLatest({
      eventId: this.store.select(getSelectedExhibitionId),
      orderUuid: this.store.select(getOrderUuid)
    }).pipe(
      first(({ eventId, orderUuid }) => !isNil(eventId) && !isEmpty(orderUuid)),
      map(({ eventId, orderUuid }) => {
        this.removeProductBookings(bookingProductTypes);

        return this.mapBookingProductTypeToPostProductBooking(bookingProductTypes, eventId, orderUuid, {});
      })
    );
  }

  removeAllProductBookings$(): Observable<{
    removeBookingProductTypes: BookingProductType[];
    removePostProductBookings: PostProductBooking[];
  }> {
    return combineLatest({
      eventId: this.store.select(getSelectedExhibitionId),
      orderUuid: this.store.select(getOrderUuid),
      bookedProductTypes: this.store.select(getBookedProducts)
    }).pipe(
      first(({ eventId, orderUuid }) => !isNil(eventId) && !isEmpty(orderUuid)),
      map(({ eventId, orderUuid, bookedProductTypes }) => {
        this.removeProductBookings(bookedProductTypes);

        return {
          removeBookingProductTypes: bookedProductTypes,
          removePostProductBookings: this.mapBookingProductTypeToPostProductBooking(
            bookedProductTypes,
            eventId,
            orderUuid,
            {}
          )
        };
      })
    );
  }

  postProductBookings$(postProductBookings: PostProductBooking[]): Observable<PostProductBookingResponse[]> {
    if (!postProductBookings.length) {
      return of([]);
    }

    return this.http.post<PostProductBookingResponse[]>(
      `${environment.protocol}${environment.webApiUrl}/order/ticket-booking`,
      postProductBookings
    );
  }

  mapBookingProductTypeToPostProductBooking(
    bookingProductTypes: BookingProductType[],
    eventId: number,
    orderUuid: string,
    sharedBookedPersonIdTariffsCount: SharedBookedPersonIdTariffsTotalCount,
    isPackageAdded = false
  ): PostProductBooking[] {
    const postProductBookings: PostProductBooking[] = [];

    bookingProductTypes.forEach((bookingProductType) => {
      if (isBookingProductTypeTariff(bookingProductType)) {
        bookingProductType.tariffs.forEach(
          ({ ticketTypeId, ticketPersonTypeId, ticketPersonId, voucherCode, count }) => {
            const currentBookedPersonIdTariffsCount: number = sharedBookedPersonIdTariffsCount[ticketPersonId] || 0;
            const postProductBooking: PostProductBooking = {
              eventId,
              ticketPersonId,
              voucherCode,
              uuid: orderUuid,
              groupId: ticketTypeId,
              ticketTypeId: ticketPersonTypeId,
              count: currentBookedPersonIdTariffsCount + count
            };

            this.setPostProductBooking(postProductBookings, postProductBooking, count);
          }
        );
      } else if (isBookingProductTypePackage(bookingProductType)) {
        bookingProductType.productTypes.forEach((bookingPackageProductType) => {
          if (isBookingProductTypeTariff(bookingPackageProductType)) {
            bookingPackageProductType.tariffs.forEach(
              ({ ticketTypeId, ticketPersonTypeId, ticketPersonId, voucherCode, count }) => {
                if (isPackageAdded && !count) {
                  return;
                }

                const currentBookedPersonIdTariffsCount: number = sharedBookedPersonIdTariffsCount[ticketPersonId] || 0;
                const postProductBooking: PostProductBooking = {
                  eventId,
                  uuid: orderUuid,
                  groupId: ticketTypeId,
                  ticketTypeId: ticketPersonTypeId,
                  ticketPersonId: ticketPersonId,
                  voucherCode: voucherCode,
                  count: currentBookedPersonIdTariffsCount + count
                };

                this.setPostProductBooking(postProductBookings, postProductBooking, count);
              }
            );
          }
        });
      }
    });

    return postProductBookings;
  }

  mapPostProductBookingsResponseToBookingProductTypes(
    bookingProductTypes: BookingProductType[],
    postProductBookingsResponse: PostProductBookingResponse[]
  ): BookingProductType[] {
    bookingProductTypes.forEach((bookingProductType) => {
      if (isBookingProductTypeTariff(bookingProductType)) {
        bookingProductType.tariffs.forEach((bookingTariff) => {
          const responseTariff = postProductBookingsResponse.find(
            (postProductBookingResponse) => postProductBookingResponse.ticketPersonId === bookingTariff.ticketPersonId
          );

          if (responseTariff) {
            bookingTariff.availableTickets = responseTariff.availableTickets;
            bookingTariff.ticketLimit = responseTariff.sellLimit;
          }
        });
      } else if (isBookingProductTypePackage(bookingProductType)) {
        bookingProductType.productTypes.forEach((bookingPackageProductType) => {
          if (isBookingProductTypeTariff(bookingPackageProductType)) {
            bookingPackageProductType.tariffs.forEach((bookingPackageTariff) => {
              const responseTariff = postProductBookingsResponse.find(
                (postProductBookingResponse) =>
                  postProductBookingResponse.ticketPersonId === bookingPackageTariff.ticketPersonId
              );

              if (responseTariff) {
                bookingPackageTariff.availableTickets = responseTariff.availableTickets;
                bookingPackageTariff.ticketLimit = responseTariff.sellLimit;
              }
            });
          }
        });
      }
    });

    return bookingProductTypes;
  }

  mapPostProductBookingsResponseToBookingPackageTypes(
    bookingPackageTypes: BookingPackageType[],
    postProductBookingsResponse: PostProductBookingResponse[]
  ): BookingPackageType[] {
    bookingPackageTypes.forEach((bookingPackageType) => {
      if (isBookingProductTypePackage(bookingPackageType)) {
        bookingPackageType.productTypes.forEach((bookingPackageProductType) => {
          if (isBookingProductTypeTariff(bookingPackageProductType)) {
            bookingPackageProductType.tariffs.forEach((bookingPackageTariff) => {
              const responseTariff = postProductBookingsResponse.find(
                (postProductBookingResponse) =>
                  postProductBookingResponse.ticketPersonId === bookingPackageTariff.ticketPersonId
              );

              if (responseTariff) {
                bookingPackageTariff.availableTickets = responseTariff.availableTickets;
                bookingPackageTariff.ticketLimit = responseTariff.sellLimit;
              }
            });
          }
        });
      }
    });

    return bookingPackageTypes;
  }

  setPostProductBooking(
    postProductBookings: PostProductBooking[],
    postProductBookingToAdd: PostProductBooking,
    count: number
  ) {
    const postProductBookingIndex = postProductBookings.findIndex(
      (existingPostProductBooking) =>
        existingPostProductBooking.ticketPersonId === postProductBookingToAdd.ticketPersonId
    );

    if (postProductBookingIndex === AppConstants.INDEX_NOT_FOUND) {
      postProductBookings.push(postProductBookingToAdd);
    } else {
      postProductBookings[postProductBookingIndex].count += count;
    }
  }

  removeProductBookings(bookingProductTypes: BookingProductType[]) {
    bookingProductTypes.forEach((bookedProductType) => {
      if (isBookingProductTypeTariff(bookedProductType)) {
        bookedProductType.tariffs.forEach((bookedTariff) => {
          bookedTariff.count = 0;
          bookedTariff.tariffUuids = [];
        });
      } else if (isBookingProductTypePackage(bookedProductType)) {
        bookedProductType.productTypes.forEach((packageProductType) => {
          if (isBookingProductTypeTariff(packageProductType)) {
            packageProductType.tariffs.forEach((packageTariff) => {
              packageTariff.count = 0;
              packageTariff.tariffUuids = [];
            });
          }
        });
      }
    });
  }
}
